/**
 * BuilderTxn.
 *
 * Section 2 of the Recurring Builder drawer. Collects the
 * transaction shape: type, counterparty, account, amount, memo.
 *
 * Party dropdown is conditional:
 *   - expense/bill -> Supplier
 *   - invoice      -> Customer
 *   - journal      -> hidden (internal)
 *   - transfer     -> hidden (handled by from/to accounts in a later PR)
 *
 * When the user picks a party or account from a dropdown, both the
 * id AND the display name are written back to the draft so the
 * Preview section can show the name without a second lookup.
 */

import type { CSSProperties } from "react";
import { BuilderSection } from "./RecurringBuilder";
import type {
  BuilderDraft,
  BuilderValidationErrors,
  BuilderContextOptions,
} from "./builderTypes";
import type { RecurringTxnType } from "../recurringTypes";

export interface BuilderTxnProps {
  draft: BuilderDraft;
  errors: BuilderValidationErrors;
  updateDraft: (partial: Partial<BuilderDraft>) => void;
  /** Dropdown data from the parent page (useSuppliers / useCustomers / useAccounts). */
  options: BuilderContextOptions;
}

interface TypePill {
  value: RecurringTxnType;
  label: string;
}

const TYPE_PILLS: TypePill[] = [
  { value: "expense", label: "Expense" },
  { value: "bill", label: "Bill" },
  { value: "invoice", label: "Invoice" },
  { value: "journal", label: "Journal" },
  { value: "transfer", label: "Transfer" },
];

export function BuilderTxn({
  draft,
  errors,
  updateDraft,
  options,
}: BuilderTxnProps) {
  const needsParty =
    draft.txnType !== "journal" && draft.txnType !== "transfer";
  const partyList =
    draft.txnType === "invoice" ? options.customers : options.suppliers;
  const partyLabel =
    draft.txnType === "invoice" ? "Customer" : "Supplier";

  // When txnType switches, clear the stale party selection so the
  // dropdown doesn't show a supplier under a customer label.
  const handleTypeChange = (value: RecurringTxnType) => {
    const typeChanged = value !== draft.txnType;
    const switchedPartyKind =
      typeChanged &&
      ((value === "invoice") !== (draft.txnType === "invoice") ||
        value === "journal" ||
        value === "transfer");
    updateDraft({
      txnType: value,
      ...(switchedPartyKind ? { partyId: null, partyName: "" } : {}),
    });
  };

  const handlePartyChange = (id: string) => {
    const opt = partyList.find((o) => o.id === id);
    updateDraft({ partyId: id || null, partyName: opt?.name ?? "" });
  };

  const handleAccountChange = (id: string) => {
    const opt = options.accounts.find((o) => o.id === id);
    updateDraft({ accountId: id || null, accountName: opt?.name ?? "" });
  };

  // Format amount on blur so "4200" becomes "4,200.00" visually.
  const handleAmountBlur = () => {
    const raw = draft.amount.replace(/,/g, "").trim();
    if (!raw) return;
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    updateDraft({
      amount: n.toLocaleString("en-CA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    });
  };

  return (
    <BuilderSection number={2} title="Transaction details">
      {/* Type pills */}
      <div style={fieldStyle}>
        <div style={fieldLabelStyle}>Type</div>
        <div style={pillRowStyle}>
          {TYPE_PILLS.map((p) => {
            const selected = draft.txnType === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => handleTypeChange(p.value)}
                aria-pressed={selected}
                style={{
                  ...pillStyle,
                  ...(selected ? pillSelectedStyle : {}),
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Party (hidden for journal/transfer) */}
      {needsParty && (
        <div style={fieldStyle}>
          <label style={fieldLabelStyle} htmlFor="builder-party">
            {partyLabel}
          </label>
          <div style={selectWrapStyle}>
            <select
              id="builder-party"
              value={draft.partyId ?? ""}
              onChange={(e) => handlePartyChange(e.target.value)}
              style={{
                ...selectInputStyle,
                ...(errors.partyId ? inputErrorStyle : {}),
              }}
            >
              <option value="">Choose a {partyLabel.toLowerCase()}...</option>
              {partyList.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
          {errors.partyId && (
            <div style={errorTextStyle}>{errors.partyId}</div>
          )}
        </div>
      )}

      {/* Account + Amount row */}
      <div style={fieldRowStyle}>
        <div style={fieldStyle}>
          <label style={fieldLabelStyle} htmlFor="builder-account">
            Account
          </label>
          <div style={selectWrapStyle}>
            <select
              id="builder-account"
              value={draft.accountId ?? ""}
              onChange={(e) => handleAccountChange(e.target.value)}
              style={{
                ...selectInputStyle,
                ...(errors.accountId ? inputErrorStyle : {}),
              }}
            >
              <option value="">Choose an account...</option>
              {options.accounts.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
          {errors.accountId && (
            <div style={errorTextStyle}>{errors.accountId}</div>
          )}
        </div>

        <div style={fieldStyle}>
          <label style={fieldLabelStyle} htmlFor="builder-amount">
            Amount
          </label>
          <div style={amountWrapStyle}>
            <span style={amountPrefixStyle}>$</span>
            <input
              id="builder-amount"
              type="text"
              inputMode="decimal"
              value={draft.amount}
              onChange={(e) => updateDraft({ amount: e.target.value })}
              onBlur={handleAmountBlur}
              placeholder="0.00"
              style={{
                ...textInputStyle,
                paddingLeft: 24,
                textAlign: "right",
                fontFamily: "ui-monospace, Menlo, monospace",
                ...(errors.amount ? inputErrorStyle : {}),
              }}
            />
          </div>
          {errors.amount && (
            <div style={errorTextStyle}>{errors.amount}</div>
          )}
        </div>
      </div>

      {/* Memo */}
      <div style={fieldStyle}>
        <label style={fieldLabelStyle} htmlFor="builder-memo">
          Memo (optional)
        </label>
        <textarea
          id="builder-memo"
          value={draft.memo}
          onChange={(e) => updateDraft({ memo: e.target.value })}
          placeholder="Shown on the posted transaction"
          style={textareaStyle}
          rows={2}
        />
      </div>
    </BuilderSection>
  );
}

// ---------- Styles ----------

const fieldStyle: CSSProperties = {
  marginBottom: 14,
};

const fieldRowStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
  marginBottom: 14,
};

const fieldLabelStyle: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "#3A4A52",
  marginBottom: 5,
  letterSpacing: "0.02em",
};

const textInputStyle: CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  border: "1px solid #D8DEE6",
  borderRadius: 7,
  fontSize: 13,
  background: "#ffffff",
  color: "#0E1A1A",
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
};

const selectInputStyle: CSSProperties = {
  ...textInputStyle,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  paddingRight: 30,
  cursor: "pointer",
};

const selectWrapStyle: CSSProperties = {
  position: "relative",
};

const inputErrorStyle: CSSProperties = {
  borderColor: "#B42318",
};

const errorTextStyle: CSSProperties = {
  marginTop: 4,
  fontSize: 11.5,
  color: "#B42318",
};

const textareaStyle: CSSProperties = {
  ...textInputStyle,
  resize: "vertical",
  minHeight: 60,
};

const pillRowStyle: CSSProperties = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
};

const pillStyle: CSSProperties = {
  padding: "7px 14px",
  border: "1px solid #D8DEE6",
  borderRadius: 999,
  fontSize: 12,
  cursor: "pointer",
  background: "#ffffff",
  color: "#3A4A52",
  fontWeight: 500,
  fontFamily: "inherit",
};

const pillSelectedStyle: CSSProperties = {
  background: "#0E1A1A",
  color: "#ffffff",
  borderColor: "#0E1A1A",
  fontWeight: 600,
};

const amountWrapStyle: CSSProperties = {
  position: "relative",
};

const amountPrefixStyle: CSSProperties = {
  position: "absolute",
  left: 12,
  top: "50%",
  transform: "translateY(-50%)",
  fontSize: 13,
  color: "#3A4A52",
  fontWeight: 500,
  pointerEvents: "none",
};

export default BuilderTxn;